import React from "react";
import { useNavigate, useParams } from "react-router-dom";

import AssignToolsModal from "./components/AssignToolsModal";
import CommentSheet from "./components/CommentSheet";
import LogSheet from "./components/LogSheet";
import AssignedTable from "./tables/AssignedTable";
import AssignToolsAwaitingTable from "./tables/AwaitingTable";
import AssignToolsOnSiteTable from "./tables/OnSiteTable";
import AssignToolsReturnTable from "./tables/ReturnTable";

import FetchWrapper from "@/components/reusables/FetchWrapper";
import CustomHeader from "@/components/reusables/Headers.tsx";
import Search from "@/components/reusables/data-table/SearchFunction";
import ButtonWithIcon from "@/components/ui/ButtonWithIcon";
import { Button } from "@/components/ui/button";
import { PROJECT_CONSTANTS } from "@/constants/projects.constant";
import { ROUTES } from "@/constants/routes_sidebar/routes-constant";
import { assignToolsTabs } from "@/constants/tabs.constant";
import DataTableSlotContextProvider from "@/context/DataTableSlot";
import useQueryFilter from "@/hooks/useQueryFilter";
import {
  IProductAssignment,
  IProjectAssignedTool,
  ProjectToolStatus,
} from "@/interfaces/tools.interface";
import {
  useGetAllProductAssignmentQuery,
  useGetToolsAssignToProjectQuery,
} from "@/redux/api/projectsQuery";

const AssignTools = (): JSX.Element => {
  const navigate = useNavigate();
  const { id: projectId } = useParams();
  const [activeTab, setActiveTab] = React.useState(assignToolsTabs[0].value);
  const [isShowAssignToolModal, setIsShowAssignModal] = React.useState(false);
  const [isShowLogSheet, setIsShowLogSheet] = React.useState(false);
  const [isShowCommentSheet, setIsShowCommentSheet] = React.useState(false);
  const { updateParams, params } = useQueryFilter("search");

  const { data, ...rest } = useGetToolsAssignToProjectQuery(
    {
      projectId: projectId!,
      params: `?status=${activeTab}&search=${params.search || ""}`,
    },
    {
      skip: !projectId || activeTab === assignToolsTabs[3].value,
    },
  );
  const { data: returnedData, ...returnedRest } =
    useGetAllProductAssignmentQuery(
      {
        status: ProjectToolStatus.RETURNED,
        projectId: projectId!,
      },
      {
        skip: !projectId || activeTab !== assignToolsTabs[3].value,
      },
    );

  const renderTable = (): JSX.Element => {
    switch (activeTab) {
      case assignToolsTabs[0].value:
        return (
          <AssignedTable data={(data?.data as IProjectAssignedTool[]) ?? []} />
        );
      case assignToolsTabs[1].value:
        return (
          <AssignToolsAwaitingTable
            data={(data?.data as IProjectAssignedTool[]) ?? []}
          />
        );
      case assignToolsTabs[2].value:
        return (
          <AssignToolsOnSiteTable
            data={(data?.data as IProjectAssignedTool[])
              ?.map((item) => item?.productAssignments)
              .flat()}
            toggleCommentSheet={() =>
              setIsShowCommentSheet(!isShowCommentSheet)
            }
          />
        );
      case assignToolsTabs[3].value:
        return (
          <AssignToolsReturnTable
            data={(returnedData?.data as IProductAssignment[]) || []}
            toggleCommentSheet={() =>
              setIsShowCommentSheet(!isShowCommentSheet)
            }
          />
        );
      default:
        return (
          <AssignedTable data={(data?.data as IProjectAssignedTool[]) ?? []} />
        );
    }
  };

  return (
    <DataTableSlotContextProvider>
      <CustomHeader
        title={PROJECT_CONSTANTS.ASSIGN_TOOLS_CONSTANTS.CONTENT_TEXT.TOOLS}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        tabs={assignToolsTabs}
        backBtn={() => navigate(ROUTES.PROJECTS)}
        custom={{
          node: (
            <section className=' flex items-center gap-3'>
              <Search
                key={"search"}
                search='search'
                updateParams={(key, value) => {
                  if (key === "search") {
                    updateParams(key, value);
                  }
                }}
              />
              {activeTab === assignToolsTabs[0].value && (
                <ButtonWithIcon onClick={() => setIsShowAssignModal(true)}>
                  {
                    PROJECT_CONSTANTS.ASSIGN_TOOLS_CONSTANTS.CONTENT_TEXT
                      .ASSIGN_TOOLS
                  }
                </ButtonWithIcon>
              )}
              <Button variant={"light"} onClick={() => setIsShowLogSheet(true)}>
                {
                  PROJECT_CONSTANTS.ASSIGN_TOOLS_CONSTANTS.CONTENT_TEXT
                    .VIEW_LOGS
                }
              </Button>
            </section>
          ),
          position: "right",
        }}
      />
      <FetchWrapper
        {...(activeTab === assignToolsTabs[3].value ? returnedRest : rest)}
        isTable
      >
        {renderTable()}
      </FetchWrapper>
      {isShowAssignToolModal && (
        <AssignToolsModal
          isOpen={isShowAssignToolModal}
          toggleAssignToolsModal={() =>
            setIsShowAssignModal(!isShowAssignToolModal)
          }
        />
      )}
      {isShowLogSheet && (
        <LogSheet
          isOpen={isShowLogSheet}
          toggleLogSheet={() => setIsShowLogSheet(!isShowLogSheet)}
          data={data?.logs || []}
        />
      )}
      {isShowCommentSheet && (
        <CommentSheet
          isOpen={isShowCommentSheet}
          toggleCommentSheet={() => setIsShowCommentSheet(!isShowCommentSheet)}
        />
      )}
    </DataTableSlotContextProvider>
  );
};

export default AssignTools;
